import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createRecurringDeductionApi,
  DEDUCTION_CALC_MODE_LABELS,
  deleteRecurringDeductionApi,
  DpDeductionCalcMode,
  DpPayrollItem,
  DpRecurringDeduction,
  listPayrollItemsApi,
  listRecurringDeductionsApi,
  PAYROLL_ITEM_KIND_LABELS,
  RecurringDeductionPayload,
  updateRecurringDeductionApi,
} from "../api/payrollApi";
import { formatCurrency, formatPercentValue } from "../utils/format";

const emptyForm = {
  itemId: "",
  calcMode: "VALOR_FIXO" as DpDeductionCalcMode,
  percent: "",
  amount: "",
  startMonth: "",
  endMonth: "",
  installments: "",
  beneficiary: "",
  notes: "",
  appliesToThirteenthAndVacation: false,
};

/** 'YYYY-MM-01' (data civil ISO) → 'MM/AAAA'. */
const monthLabel = (iso: string | null) => {
  if (!iso) return null;
  const [year, month] = iso.slice(0, 7).split("-");
  return `${month}/${year}`;
};

const describeValue = (d: DpRecurringDeduction) =>
  d.calcMode === "VALOR_FIXO"
    ? formatCurrency(d.amount)
    : `${formatPercentValue(d.percent)} ${DEDUCTION_CALC_MODE_LABELS[d.calcMode].replace(/^% /, "")}`;

const describePeriod = (d: DpRecurringDeduction) => {
  const start = monthLabel(d.startMonth);
  if (d.installments) return `${start} · ${d.installments} parcela(s)`;
  if (d.endMonth) return `${start} a ${monthLabel(d.endMonth)}`;
  return `desde ${start}`;
};

type Props = { token: string; contractId: string; readOnly: boolean };

/**
 * Card "Descontos em folha" da aba Contrato: pensão, consignado, sindical, plano de
 * saúde — tudo que se repete todo mês. Usado pela folha de conferência (F1).
 */
export default function RecurringDeductionsCard({ token, contractId, readOnly }: Props) {
  const [rows, setRows] = useState<DpRecurringDeduction[]>([]);
  const [items, setItems] = useState<DpPayrollItem[]>([]);
  const [modal, setModal] = useState<{ row?: DpRecurringDeduction } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listRecurringDeductionsApi(token, contractId));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os descontos em folha.");
    }
  }, [token, contractId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (readOnly) return;
    listPayrollItemsApi(token)
      .then(setItems)
      .catch(() => setItems([]));
  }, [token, readOnly]);

  const manualItems = useMemo(() => items.filter((i) => i.isManual && i.isActive), [items]);

  const openModal = (row?: DpRecurringDeduction) => {
    setForm(
      row
        ? {
            itemId: row.itemId,
            calcMode: row.calcMode,
            percent: row.percent === null ? "" : String(Number(row.percent)),
            amount: row.amount === null ? "" : String(Number(row.amount)),
            startMonth: row.startMonth.slice(0, 7),
            endMonth: row.endMonth ? row.endMonth.slice(0, 7) : "",
            installments: row.installments ? String(row.installments) : "",
            beneficiary: row.beneficiary ?? "",
            notes: row.notes ?? "",
            appliesToThirteenthAndVacation: row.appliesToThirteenthAndVacation,
          }
        : emptyForm
    );
    setModal({ row });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (!form.itemId) return toast.error("Escolha a rubrica.");
    if (!form.startMonth) return toast.error("Informe a primeira competência.");
    const isFixed = form.calcMode === "VALOR_FIXO";
    const isAlimony = manualItems.find((i) => i.id === form.itemId)?.code === "PENSAO_ALIMENTICIA";
    const payload: RecurringDeductionPayload = {
      itemId: form.itemId,
      calcMode: form.calcMode,
      amount: isFixed ? Number(form.amount) : null,
      percent: isFixed ? null : Number(form.percent),
      startMonth: form.startMonth,
      endMonth: form.endMonth || null,
      installments: form.installments ? Number(form.installments) : null,
      beneficiary: form.beneficiary.trim() || null,
      notes: form.notes.trim() || null,
      // Só a pensão desconta no 13º e nas férias (decisão comum 4 do plano F2); consignado e sindical não.
      appliesToThirteenthAndVacation: isAlimony && form.appliesToThirteenthAndVacation,
    };
    setSaving(true);
    try {
      if (modal.row) await updateRecurringDeductionApi(token, modal.row.id, payload);
      else await createRecurringDeductionApi(token, contractId, payload);
      toast.success("Desconto salvo. Vale nas competências abertas ao recalcular.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o desconto.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: DpRecurringDeduction) => {
    if (!window.confirm(`Excluir o desconto "${row.item.name}"?`)) return;
    try {
      await deleteRecurringDeductionApi(token, row.id);
      toast.success("Desconto excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o desconto.");
    }
  };

  return (
    <div className="dpSection" style={{ marginBottom: 16 }}>
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h3 className="dpSection__title" style={{ margin: 0 }}>
          Descontos em folha
        </h3>
        {!readOnly ? (
          <button className="dpBtn dpBtn--secondary" onClick={() => openModal()}>
            Novo desconto
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="dpFormHint">Nenhum desconto recorrente (pensão, consignado, sindical, plano de saúde).</p>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Rubrica</th>
                <th>Valor</th>
                <th>Período</th>
                <th>Beneficiário</th>
                {!readOnly ? <th style={{ width: 160 }}>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.item.name}
                    {row.notes ? <div className="dpFormHint">{row.notes}</div> : null}
                    {row.appliesToThirteenthAndVacation ? <div className="dpFormHint">Desconta também no 13º e nas férias</div> : null}
                  </td>
                  <td>{describeValue(row)}</td>
                  <td>{describePeriod(row)}</td>
                  <td>{row.beneficiary || "—"}</td>
                  {!readOnly ? (
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openModal(row)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => remove(row)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.row ? "Editar desconto em folha" : "Novo desconto em folha"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="rdItem">Rubrica *</label>
                  <select
                    id="rdItem"
                    className="dpSelect"
                    value={form.itemId}
                    onChange={(e) => setForm({ ...form, itemId: e.target.value })}
                    disabled={saving}
                  >
                    <option value="">Selecione</option>
                    {manualItems.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({PAYROLL_ITEM_KIND_LABELS[i.kind].toLowerCase()})
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">Plano de saúde e outros: crie a rubrica em Cadastros → Rubricas.</span>
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="rdMode">Forma de cálculo *</label>
                    <select
                      id="rdMode"
                      className="dpSelect"
                      value={form.calcMode}
                      onChange={(e) => setForm({ ...form, calcMode: e.target.value as DpDeductionCalcMode })}
                      disabled={saving}
                    >
                      {(Object.keys(DEDUCTION_CALC_MODE_LABELS) as DpDeductionCalcMode[]).map((mode) => (
                        <option key={mode} value={mode}>
                          {DEDUCTION_CALC_MODE_LABELS[mode]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {form.calcMode === "VALOR_FIXO" ? (
                    <div className="dpFormGroup">
                      <label htmlFor="rdAmount">Valor mensal (R$) *</label>
                      <input
                        id="rdAmount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="dpInput"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        disabled={saving}
                      />
                    </div>
                  ) : (
                    <div className="dpFormGroup">
                      <label htmlFor="rdPercent">Percentual (%) *</label>
                      <input
                        id="rdPercent"
                        type="number"
                        step="0.0001"
                        min="0.0001"
                        max="100"
                        className="dpInput"
                        value={form.percent}
                        onChange={(e) => setForm({ ...form, percent: e.target.value })}
                        disabled={saving}
                      />
                    </div>
                  )}
                </div>
                {form.calcMode === "PERCENTUAL_LIQUIDO" ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    Líquido = bruto − INSS − IR, como costuma constar no ofício de pensão.
                  </p>
                ) : null}
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="rdStart">Primeira competência *</label>
                    <input
                      id="rdStart"
                      type="month"
                      className="dpInput"
                      value={form.startMonth}
                      onChange={(e) => setForm({ ...form, startMonth: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="rdEnd">Última competência</label>
                    <input
                      id="rdEnd"
                      type="month"
                      className="dpInput"
                      value={form.endMonth}
                      onChange={(e) => setForm({ ...form, endMonth: e.target.value, installments: "" })}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="rdInstallments">ou nº de parcelas</label>
                    <input
                      id="rdInstallments"
                      type="number"
                      step="1"
                      min="1"
                      className="dpInput"
                      value={form.installments}
                      onChange={(e) => setForm({ ...form, installments: e.target.value, endMonth: "" })}
                      disabled={saving}
                    />
                  </div>
                </div>
                {manualItems.find((i) => i.id === form.itemId)?.code === "PENSAO_ALIMENTICIA" ? (
                  <label className="dpCheckbox">
                    <input
                      type="checkbox"
                      checked={form.appliesToThirteenthAndVacation}
                      onChange={(e) => setForm({ ...form, appliesToThirteenthAndVacation: e.target.checked })}
                      disabled={saving}
                    />
                    Desconta também no 13º e nas férias
                  </label>
                ) : null}
                <div className="dpFormGroup">
                  <label htmlFor="rdBeneficiary">Beneficiário / credor</label>
                  <input
                    id="rdBeneficiary"
                    className="dpInput"
                    maxLength={200}
                    placeholder="Pensão: quem recebe. Consignado: banco."
                    value={form.beneficiary}
                    onChange={(e) => setForm({ ...form, beneficiary: e.target.value })}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="rdNotes">Observação</label>
                  <textarea
                    id="rdNotes"
                    className="dpTextarea"
                    maxLength={500}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
